import type { MensagemDTO } from '@viniko/shared';
import { FileText, Send, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useToast } from '../../componentes/ui';
import { enviarArquivo } from '../../lib/api';

export const tamanhoLegivel = (b: number) =>
  b < 1024
    ? `${b} B`
    : b < 1024 * 1024
      ? `${Math.round(b / 1024)} KB`
      : `${(b / 1024 / 1024).toFixed(1)} MB`;

export function PreviaEnvio({
  conversaId,
  arquivos: iniciais,
  respondeAId,
  fechar,
  aoEnviar,
}: {
  conversaId: string;
  arquivos: File[];
  respondeAId?: string;
  fechar: () => void;
  aoEnviar: () => void;
}) {
  const toast = useToast();
  const [arquivos, setArquivos] = useState(iniciais);
  const [atual, setAtual] = useState(0);
  const [legenda, setLegenda] = useState('');
  const [enviando, setEnviando] = useState(false);

  const [urls, setUrls] = useState<(string | null)[]>([]);
  useEffect(() => {
    const u = arquivos.map((a) =>
      a.type.startsWith('image/') || a.type.startsWith('video/') ? URL.createObjectURL(a) : null,
    );
    setUrls(u);
    return () => u.forEach((x) => x && URL.revokeObjectURL(x));
  }, [arquivos]);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && fechar();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [fechar]);

  if (!arquivos.length) return null;
  const sel = arquivos[Math.min(atual, arquivos.length - 1)]!;
  const selUrl = urls[Math.min(atual, arquivos.length - 1)];

  function remover(i: number) {
    const resto = arquivos.filter((_, j) => j !== i);
    if (!resto.length) return fechar();
    setArquivos(resto);
    setAtual((a) => Math.max(0, Math.min(a, resto.length - 1)));
  }

  async function enviar() {
    setEnviando(true);
    try {
      for (const [i, a] of arquivos.entries()) {
        await enviarArquivo<MensagemDTO>(conversaId, a, {
          legenda: i === 0 ? legenda.trim() || undefined : undefined,
          respondeAId: i === 0 ? respondeAId : undefined,
        });
      }
      aoEnviar();
      fechar();
    } catch (e) {
      toast((e as Error).message, 'erro');
      setEnviando(false);
    }
  }

  return (
    <div className="previa" role="dialog" aria-label="Enviar arquivo">
      <header className="previa-topo">
        <button className="icone-btn sem-borda" aria-label="Cancelar" onClick={fechar}>
          <X />
        </button>
        <span className="previa-nome">{sel.name || 'Imagem colada'}</span>
      </header>

      <div className="previa-centro">
        {selUrl && sel.type.startsWith('image/') ? (
          <img src={selUrl} alt="Prévia" />
        ) : selUrl && sel.type.startsWith('video/') ? (
          <video src={selUrl} controls />
        ) : (
          <div className="previa-doc">
            <FileText />
            <b>{sel.name}</b>
            <span>
              {tamanhoLegivel(sel.size)} · {sel.type || 'arquivo'}
            </span>
          </div>
        )}
      </div>

      <div className="previa-base">
        <input
          id="previa-legenda"
          className="previa-legenda"
          placeholder="Adicione uma legenda"
          value={legenda}
          autoFocus
          onChange={(e) => setLegenda(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !enviando && void enviar()}
        />
        <div className="previa-linha">
          <div className="previa-miniaturas">
            {arquivos.map((a, i) => (
              <div key={i} className={`mini ${i === atual ? 'sel' : ''}`}>
                <button
                  className="mini-abrir"
                  onClick={() => setAtual(i)}
                  aria-label={`Ver ${a.name}`}
                >
                  {urls[i] && a.type.startsWith('image/') ? (
                    <img src={urls[i]!} alt="" />
                  ) : (
                    <FileText />
                  )}
                </button>
                <button
                  className="mini-remover"
                  aria-label={`Remover ${a.name}`}
                  onClick={() => remover(i)}
                >
                  <X />
                </button>
              </div>
            ))}
          </div>
          <button
            className="enviar grande"
            aria-label="Enviar"
            disabled={enviando}
            onClick={() => void enviar()}
          >
            <Send />
            {arquivos.length > 1 && <span className="enviar-n">{arquivos.length}</span>}
          </button>
        </div>
      </div>
    </div>
  );
}
